import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material';
import { Typography, Box, Stack, Pagination, Skeleton } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import User, { Company, Contact, Service } from '../../../../interfaces/users.interface';
import { DataCell, DataRow } from '../../../Tables/DataList';
import { tightTableSx } from '../../../Tables/dataTableStyles';
import { useAppSelector } from '../../../../app/hooks';
import SiteRowWithWorkflow from './SiteRowWithWorkflow';
import SiteSidebar from './SiteSidebar';
import LocationForm from '../LocationForm';
import StandardDialog from '../../../Modals/StandardDialog';
import { useCompanySites } from './useCompanySites';
import { useCompanyResource } from '../../../Vendors/useCompanyResource';
import { getCompanySites, getCompanyServices, getCompanyUsers } from '../../../../services/companyDetail';

const PAGE_SIZE = 5;
const ALL = 9999;

type SitesTabProps = {
  company: Company;
  companyCallback: any;
  userCanAdminCompany: boolean;
  createSiteOpen?: boolean;
  onCreateSiteOpen?: () => void;
  onCreateSiteClose?: () => void;
};

export default function SitesTab(props: SitesTabProps) {
  const theme = useTheme();
  const { company, companyCallback, userCanAdminCompany, createSiteOpen: externalCreateSiteOpen, onCreateSiteClose } = props;
  const { workflows: fullWf } = useAppSelector((state) => state.workflow);

  const [internalCreateSiteOpen, setInternalCreateSiteOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [canStartNewQuestionniare, setCanStartNewQuestionniare] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Contact | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Use external dialog state if provided, otherwise fall back to internal state.
  const createSiteOpen = externalCreateSiteOpen !== undefined ? externalCreateSiteOpen : internalCreateSiteOpen;

  const { sites, count, loading, refetch } = useCompanySites(company.id, currentPage, PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  // Site rows/sidebar read each site's embedded app names + certifications from
  // the sites endpoint itself, so no company-wide apps/certs fetch is needed. The
  // full site list only feeds the add form and the sidebar's edit/delete, so
  // defer it until one of those is open.
  // The add/edit site form's selectors need the full site list, the company's
  // employees (primary contact), and its services; fetch all only when the
  // form/sidebar is open.
  const formDataEnabled = createSiteOpen || isSidebarOpen;
  const { items: allSites } = useCompanyResource<Contact>(getCompanySites, company.id, 1, ALL, formDataEnabled);
  const { items: employees } = useCompanyResource<User>(getCompanyUsers, company.id, 1, ALL, formDataEnabled);
  const { items: services } = useCompanyResource<Service>(getCompanyServices, company.id, 1, ALL, formDataEnabled);
  const cardCompany = useMemo(
    () => ({ ...company, locations: allSites, employees, services }),
    [company, allSites, employees, services],
  );

  // Clamp to the last available page if rows were removed (e.g. after a delete).
  useEffect(() => {
    if (!loading && currentPage > totalPages) setCurrentPage(totalPages);
  }, [loading, currentPage, totalPages]);

  const handleCreateSiteClose = useCallback(() => {
    if (onCreateSiteClose) {
      onCreateSiteClose();
    } else {
      setInternalCreateSiteOpen(false);
    }
  }, [onCreateSiteClose]);

  const handleSetCanStartNewQuestionniare = useCallback(() => {
    setCanStartNewQuestionniare(true);
  }, []);

  const handleRowClick = useCallback((site: Contact) => {
    setSelectedSite(site);
    setIsSidebarOpen(true);
  }, []);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
    setSelectedSite(null);
  }, []);

  const handleSiteCreation = useCallback(
    (companyResponse: Company) => {
      companyCallback(companyResponse);
      refetch();
      handleCreateSiteClose();
    },
    [companyCallback, refetch, handleCreateSiteClose],
  );

  const handleSuccessfulSiteEdit = useCallback((companyResponse: Company, updateType: 'edit' | 'delete') => {
    companyCallback(companyResponse);
    refetch();
    const currentSite = companyResponse.locations?.find((site) => site.id === selectedSite?.id);
    if (currentSite) {
      setSelectedSite(currentSite);
    }
    if (updateType === 'delete') {
      handleSidebarClose();
    }
  }, [companyCallback, refetch, handleSidebarClose, selectedSite?.id]);

  return (
    <>
      <Stack spacing={2} sx={{ p: 2 }}>
        <StandardDialog title='Add New Site' handleClose={handleCreateSiteClose} isOpen={createSiteOpen}>
          <LocationForm
            submitButtonText='Add Site'
            company={cardCompany}
            successCallback={handleSiteCreation}
            modalClose={() => handleCreateSiteClose()}
          />
        </StandardDialog>

        <Typography
          variant="body1"
          sx={{
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            fontSize: '0.95rem',
            padding: 2,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <InfoOutlined fontSize="inherit" />
          Click the site row to see more details in the sidebar
        </Typography>

        <Box sx={tightTableSx}>
          <DataRow header>
            <DataCell xs={1} textAlign='center'>
              Shield
            </DataCell>
            <DataCell xs={3}>Name</DataCell>
            <DataCell xs={2}>Applications</DataCell>
            <DataCell xs={2}>Services</DataCell>
            <DataCell xs={1}>Status</DataCell>
            <DataCell xs={2} textAlign='center'>
              Actions
            </DataCell>
            <DataCell xs={1}>&nbsp;</DataCell>
          </DataRow>

          {loading &&
            Array.from({ length: PAGE_SIZE }).map((_, index) => (
              <DataRow key={`skeleton-${index}`}>
                <DataCell xs={1} textAlign='center'><Skeleton variant='circular' width={28} height={28} sx={{ mx: 'auto' }} /></DataCell>
                <DataCell xs={3}><Skeleton variant='text' /></DataCell>
                <DataCell xs={2}><Skeleton variant='text' /></DataCell>
                <DataCell xs={2}><Skeleton variant='text' /></DataCell>
                <DataCell xs={1}><Skeleton variant='text' /></DataCell>
                <DataCell xs={2}><Skeleton variant='text' /></DataCell>
                <DataCell xs={1}>&nbsp;</DataCell>
              </DataRow>
            ))}

          {!loading && sites.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'>No sites</Typography>
            </Box>
          )}

          {!loading &&
            sites.map((site: Contact) => (
              <SiteRowWithWorkflow
                key={site.id}
                company={cardCompany}
                site={site}
                fullWf={fullWf}
                userCanAdminCompany={userCanAdminCompany}
                companyCallback={companyCallback}
                onRowClick={handleRowClick}
                handleSetCanStartNewQuestionniare={handleSetCanStartNewQuestionniare}
              />
            ))}
        </Box>

        {count > PAGE_SIZE && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, value) => setCurrentPage(value)}
              showFirstButton
              showLastButton
              shape='circular'
              sx={{
                '& .MuiPaginationItem-root': {
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#135ea7',
                  },
                },
              }}
            />
          </Box>
        )}
      </Stack>

      <SiteSidebar
        canStartNewQuestionniare={canStartNewQuestionniare}
        isOpen={isSidebarOpen}
        company={cardCompany}
        site={selectedSite}
        onClose={handleSidebarClose}
        companyCallback={handleSuccessfulSiteEdit}
      />
    </>
  );
}
